import type { QueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { FeatureLockDialog } from "@/components/billing/feature-lock-dialog";
import { OutOfCreditsDialog } from "@/components/billing/out-of-credits-dialog";
import { PlanLimitDialog } from "@/components/billing/plan-limit-dialog";
import { TrialBar } from "@/components/billing/trial-bar";
import { AppSidebar } from "@/components/dashboard/app-sidebar";
import { SetupGate } from "@/components/onboarding/setup-gate";
import { ViewerBanner } from "@/components/workspace/viewer-banner";
import { accountPlanQuery } from "@/core/hooks/use-account-plan";
import { requireView } from "@/core/lib/guards";
import { SessionProvider } from "@/core/lib/session-context";
import { isCompanyComplete } from "@/lib/onboarding";
import { atLeast, hasDashboard, homeFor, pageRequirement } from "@/lib/permissions";
import { workspaceAccessQuery } from "@/lib/team-api";
import { companyQuery } from "@/lib/workspace-data";
import { ImpersonationBanner } from "@/ui/components/auth/impersonation-banner";
import { TopHeader } from "@/ui/components/dashboard/top-header";
import { GuardedShellPending } from "@/ui/components/skeletons/route-pending";
import { SidebarInset, SidebarProvider } from "@/ui/components/ui/sidebar";

/**
 * The HR workspace shell — the port of the `(users)` server layout.
 *
 * Honors the chosen surface: an admin may opt into the HR workspace, but
 * anyone whose active view isn't "user" (a plain admin, an employee, or an
 * account with no company membership) is sent to their own home — across
 * origins when that home is the admin app.
 *
 * The layout is pathless so the workspace routes sit at top-level
 * URLs (`/`, `/employees`, `/alerts`, …) rather than gaining a segment.
 */
export const Route = createFileRoute("/_workspace")({
  beforeLoad: async ({ context, location }) => {
    const auth = await requireView(context.queryClient, "user", "/sign-in", location.href);
    // The loader below awaits these; starting them now, alongside the role
    // check, saves a whole round trip on the way in. `prefetchQuery` shares
    // the in-flight request with the loader's `ensureQueryData`.
    void context.queryClient.prefetchQuery(companyQuery);
    void context.queryClient.prefetchQuery(accountPlanQuery);
    await requirePageAccess(
      context.queryClient,
      auth.session.session.activeOrganizationId ?? null,
      location.pathname,
    );
    return { auth };
  },
  loader: async ({ context }) => {
    // Awaited together so the shell paints once, with the right plan tier and
    // the right gate decision, instead of settling into them.
    await Promise.all([
      context.queryClient.ensureQueryData(companyQuery),
      context.queryClient.ensureQueryData(accountPlanQuery),
    ]);
  },
  component: WorkspaceLayout,
  // The guard and the loader above are two network waves, and until both land
  // there is no shell to show. The shell's outline stands in once a session is
  // known — never for a visitor the guard is about to send to `/sign-in`.
  pendingComponent: GuardedShellPending,
});

/**
 * Keeps a role out of pages it has no access to, for every workspace route at
 * once: `beforeLoad` here runs on each navigation into the workspace. A page
 * the role can't open — or the dashboard, for a role with none of the areas it
 * summarises — sends them to the first area they can see.
 *
 * The API refuses the same requests; this only stops the page loading into a
 * screen of errors. If the role can't be read it lets the page load, so a
 * network blip degrades to the API's own refusals rather than a lockout.
 */
async function requirePageAccess(
  queryClient: QueryClient,
  orgId: string | null,
  pathname: string,
): Promise<void> {
  if (!orgId) return;
  const access = await queryClient.ensureQueryData(workspaceAccessQuery(orgId)).catch(() => null);
  const permissions = access?.permissions;
  if (!permissions) return;

  // Billing and usage are the paying owner's, not a role's to grant.
  if (!access?.isOwner && BILLING_PAGES.some((page) => pathname.startsWith(page))) {
    throw redirect({ to: "/settings", replace: true });
  }

  const need = pageRequirement(pathname);
  const blocked =
    (need !== null && !atLeast(permissions[need.area], need.level)) ||
    (pathname === "/" && !hasDashboard(permissions));
  if (blocked) {
    const home = homeFor(permissions);
    if (home !== pathname) throw redirect({ to: home, replace: true });
  }
}

const BILLING_PAGES = ["/settings/billing", "/settings/usage"];

/** Mirrors the `sidebar_state` cookie the server layout used to read. */
function sidebarDefaultOpen(): boolean {
  if (typeof document === "undefined") return true;
  return !document.cookie.split("; ").some((c) => c === "sidebar_state=false");
}

function WorkspaceLayout() {
  const { auth } = Route.useRouteContext();
  const access = useQuery(
    workspaceAccessQuery(auth.session.session.activeOrganizationId ?? null),
  ).data;
  // Personal workspace, or a role still loading: keep the bell. The guard
  // above has usually filled this already.
  const showAlerts = !access?.permissions || atLeast(access.permissions.alerts, "view");
  const showBilling = !auth.session.session.activeOrganizationId || Boolean(access?.isOwner);
  const { data: company } = useQuery(companyQuery);

  // Post-registration setup gate: block the workspace behind a non-dismissible
  // dialog until the company profile is complete. `company` is null when the
  // backend is unreachable — treat that as "can't tell" and skip the gate
  // rather than lock the user out of a workspace they'd have no way to fix.
  const backendReachable = company !== null && company !== undefined;
  const needsSetup = backendReachable && !isCompanyComplete(company);

  return (
    <SessionProvider initial={auth.session}>
      <SidebarProvider defaultOpen={sidebarDefaultOpen()}>
        <AppSidebar />
        <SidebarInset>
          {showBilling ? <TrialBar /> : null}
          <TopHeader showNotifications={showAlerts} showBilling={showBilling} />
          <ImpersonationBanner session={auth.session} />
          <ViewerBanner />
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
      {needsSetup ? <SetupGate company={company} /> : null}
      <OutOfCreditsDialog />
      <FeatureLockDialog />
      <PlanLimitDialog />
      {/* Offered to whoever pays for the account — never a teammate — and not
          while the setup gate still has them. */}
    </SessionProvider>
  );
}
